import qs from 'qs';
import Vue from 'vue';
import VueRouter from 'vue-router';
import i18n from '@/js/i18n';
import store from '@/store';
import { bus } from '@/main';

import routesDepot from './depot';
import routesCasino from './casino';

import PageLoader from '@/helpers/PageLoader';

import { getIdTokenClaims } from '@/services/api/auth/authService';
import session from '@/services/api/compte/session';
import promo from '@/services/api/compte/promo';
import encadrerJeux from '@/services/api/compte/encadrerJeux';
import { getDomain } from '@/services/api/jel/cookie';
import {
  processSalesforceClientMigrated,
  loadPlayerPaymentProfile,
} from '@/services/api/compte/profile';

import { inMgnl, inAppMobile, nettoyerLienExterne, isJelMobile } from '@/helpers/AppHelpers';
import MessageHelpers from '@/helpers/MessageHelpers';
import { urlsDest, PageMapping, PageMappingCasino } from '@/helpers/PageMappingHelpers';
import {
  ProductSourceInfo,
  isProductSource2eChance,
  getProductSourceFromQueryParams,
  arrProductSource2eChance,
} from '@/helpers/ProductSourceHelpers';
import { playerPaymentProfileStatus } from '@/helpers/ProfileHelpers';
import { isPreferencesPath, returnToHome } from '@/helpers/RouteHelpers';

Vue.use(VueRouter);

const baseRoutes = [
  {
    path: process.env.VUE_APP_BASE_URL,
    component: PageLoader,
    redirect: PageMapping.BIENVENUE.siteUrl,
  },
  {
    path: '*',
    component: PageLoader,
  },
];

// baseRoutes doit être en dernier pour attraper "*"
// const allRoutes = routesDepot.concat(routesPromo).concat(baseRoutes); (2021-06-19 1B? peu de chance)
const allRoutes = routesCasino.concat(routesDepot.concat(baseRoutes));
const newRegFlow = process.env.VUE_APP_NEW_REG_FLOW === 'true';

// TODO: Déterminer si bonne solution
// Inspiration: https://github.com/vuejs/vue-router/blob/dev/examples/scroll-behavior/app.js
// scrollBehavior:
// - only available in html5 history mode
// - defaults to no scroll behavior
// - return false to prevent scroll
const scrollBehavior = function (to, from, savedPosition) {
  if (savedPosition) {
    // savedPosition is only available for popstate navigations.
    return savedPosition;
  }
  const position = {};

  // scroll to anchor by returning the selector
  if (to.hash) {
    position.selector = to.hash;

    // specify offset of the element
    if (to.hash === '#anchor2') {
      position.offset = { y: 100 };
    }

    // bypass #1number check
    if (!/^#_ga/.test(to.hash) && (/^#\d/.test(to.hash) || document.querySelector(to.hash))) {
      return position;
    }

    // if the returned position is falsy or an empty object,
    // will retain current scroll position.
    return false;
  }

  return new Promise((resolve) => {
    // check if any matched route config has meta that requires scrolling to top
    if (to.matched.some((m) => m.meta.scrollToTop)) {
      // coords will be used if no selector is provided,
      // or if the selector didn't match any element.
      position.x = 0;
      position.y = 0;
    }

    // wait for the out transition to complete (if necessary)
    this.app.$root.$once('triggerScroll', () => {
      // if the resolved position is falsy or an empty object,
      // will retain current scroll position.
      resolve(position);
    });
  });
};

const router = new VueRouter({
  mode: 'history',
  base: i18n.locale,
  scrollBehavior,
  routes: allRoutes,
});

// TODO: restructurer le router
router.beforeEach(async (to, from, next) => {
  const routeFrom = store.getters['routes/getRouteByPath'](from.path);
  const route = store.getters['routes/getRouteByPath'](to.path);
  const isSessionEstOuverte = session.sessionEstOuverte();
  const hasProductSourceParam = getProductSourceFromQueryParams() != null;

  // Ajoute url de 'from.path' dans routerHistory
  if (from.path != to.path && !to.path.startsWith('/erreur/')) {
    store.commit('routes/ADD_ROUTE', to);
  }

  // Page transition
  if (to.path === store.getters['routes/getUrlFromWebsitePath'](PageMapping.TRANSITION.siteUrl)) {
    if (to.query.error === 'access_denied') {
      return next({
        path: store.getters['routes/getUrlFromWebsitePath'](PageMapping.ERREUR_GENERIQUE.siteUrl),
      });
    }

    const prdSrc = store.state.compte.productSource;

    const targetPage = arrProductSource2eChance.includes(prdSrc)
      ? PageMapping.ADRESSE_2E_CHANCE.siteUrl
      : PageMapping.RESTONS_EN_CONTACT.siteUrl;

    const baseUrl = `${process.env.VUE_APP_PROXY_URL_POD}/${i18n.locale}`;

    const getUrl = (path) => `${baseUrl}${store.getters['routes/getUrlFromWebsitePath'](path)}`;

    const url = getUrl(targetPage);
    const link = getUrl(PageMapping.IDENTITE_REPRISE.siteUrl);

    const domain = getDomain();
    document.cookie = `Dossier.clientMigrated=true;domain=${domain};path=/;Secure;SameSite=Lax`;

    if (to.query.action === 'connect') {
      // eslint-disable-next-line no-undef
      lancerConnexionAuth0(i18n.locale, link, false);
    } else {
      // eslint-disable-next-line no-undef
      lancerConnexionAuth0(i18n.locale, url, true);
    }
    return next(false);
  }

  // Validation du code promo à partir de la page Bienvenue
  if (to.path === store.getters['routes/getUrlFromWebsitePath'](PageMapping.BIENVENUE.siteUrl)) {
    const { promo: code } = to.query;

    if (code !== undefined) {
      if (code) {
        try {
          // Validation de l’offre promotionnelle
          await promo.validerPromo(code);
          store.commit('promo/SET_IS_PROMO_NOT_VALID', false);
        } catch (error) {
          // Redirection vers la page Bienvenue sans paramètres
          store.commit('promo/SET_IS_PROMO_NOT_VALID', true);
          return next({ path: to.path, query: {}, replace: true });
        }
      } else {
        // promo présent mais vide → Redirection vers la page Bienvenue sans paramètres
        store.commit('promo/SET_IS_PROMO_NOT_VALID', true);
        return next({ path: to.path, query: {}, replace: true });
      }
    }
  }

  // *****  le nouveau parcours d’inscription avec Salesforce 2ème chance *****
  if (
    to.path ===
      store.getters['routes/getUrlFromWebsitePath'](PageMapping.ADRESSE_2E_CHANCE.siteUrl) &&
    newRegFlow
  ) {
    const prdSrc = store.state.compte.productSource;

    if (session.sessionEstOuverte()) {
      if (store.state.dossier.clientMigrated === null) {
        try {
          await processSalesforceClientMigrated();
        } catch (error) {
          return next({
            path: store.getters['routes/getUrlFromWebsitePath'](error.url),
          });
        }
      }

      if (prdSrc && !to.query.product_source) {
        return next({ ...to, query: { ...to.query, product_source: prdSrc } });
      }
    } else {
      // CCC-18999
      session.cleanUpSession();
      returnToHome();
      return next(false);
    }
  }

  // ***** le nouveau parcours d’inscription avec Salesforce Restons en Contact *****
  if (
    (to.path ===
      store.getters['routes/getUrlFromWebsitePath'](PageMapping.RESTONS_EN_CONTACT.siteUrl) ||
      to.path ===
        store.getters['routes/getUrlFromWebsitePath'](
          PageMapping.RESTONS_EN_CONTACT_OFFRE.siteUrl
        )) &&
    newRegFlow
  ) {
    const prdSrc = store.state.compte.productSource;

    if (session.sessionEstOuverte() && !store.state.dossier.clientMigrated) {
      try {
        await processSalesforceClientMigrated();
      } catch (error) {
        return next({
          path: store.getters['routes/getUrlFromWebsitePath'](error.url),
        });
      }
    }

    if (prdSrc && !to.query.product_source) {
      return next({ ...to, query: { ...to.query, product_source: prdSrc } });
    }
  }

  console.log('[router] beforeEach to:', to.path, '| from:', from.path, '| inMgnl:', inMgnl, '| isRouting:', isRouting(), '| route trouvée:', !!route);
  if (inMgnl || !isRouting()) {
    // Pas de protection dans Magnolia
    next();
  } else if (to.path == from.path) {
    /*
     * Permettre changement de salle Bingo (idSalle passé en QueryParam)
     * On ajoute le test 'from.query != to.query'
     */
    if (hasQueryParams(from) && hasQueryParams(to)) {
      next(from.query != to.query);
    } else {
      bus.$emit('afficherMessage');
      next(to.hash != from.hash);
    }
  } else if (!route) {
    // On laisse passer pour toutes les routes casino/(collection|produit)/*
    // TODO: Centraliser cette logique
    if (isSessionEstOuverte && isPreferencesPath(to.path)) {
      store.dispatch('preferences/getPreferences');
    }
    if (
      to.path.startsWith(PageMappingCasino.CASINO_COLLECTION.siteUrl) ||
      to.path.startsWith(PageMappingCasino.CASINO_COLLECTION.siteUrlEn) ||
      to.path.startsWith(PageMappingCasino.CASINO_PRODUIT.siteUrl) ||
      to.path.startsWith(PageMappingCasino.CASINO_PRODUIT.siteUrlEn)
    ) {
      next(true);
    } else {
      console.warn(`router: Route inexistante: ${to.path}`);
      next(false);
    }
  } else if (hasQueryParams(to) && !hasProductSourceParam && to.query.opl == 1) {
    // IMPORTANT: Ceci a pour effet de faire une redirection qui entrera à nouveau dans router.beforeEach
    // opl=1 => afficher connexion puis redirection vers la destination
    // const isSessionEstOuverte = session.sessionEstOuverte();
    if (isSessionEstOuverte) {
      if (to.query.dest) {
        const { dest } = to.query;
        // On supprime les query params avant la redirection
        delete to.query.opl;
        delete to.query.dest;

        if (dest === 'autoexclusion') {
          store
            .dispatch('profile/getProfileDetails', { depositInfo: false, withdrawInfo: false })
            .then((response) => {
              const profile = response.playerPaymentProfile || response;
              if (profile.status?.includes(playerPaymentProfileStatus.GUEST)) {
                next({
                  path: store.getters['routes/getUrlFromWebsitePath'](
                    PageMapping.IDENTITE_REPRISE.siteUrl
                  ),
                });
                return;
              }
              const urlDest = redirectionDest(to, dest);
              if (!urlDest) {
                window.location.href = nettoyerLienExterne(erreurDefaut.url);
              } else {
                window.location.href = nettoyerLienExterne(`/${i18n.locale}${urlDest}`);
              }
            })
            .catch(() => {
              const urlDest = redirectionDest(to, dest);
              if (!urlDest) {
                window.location.href = nettoyerLienExterne(erreurDefaut.url);
              } else {
                window.location.href = nettoyerLienExterne(`/${i18n.locale}${urlDest}`);
              }
            });
          return;
        }

        const urlDest = redirectionDest(to, dest);

        if (!urlDest) {
          // On redirige vers la page d'accueil du Portail LQ
          window.location.href = nettoyerLienExterne(erreurDefaut.url);
        } else if (
          // Ce n'est pas une url Magnolia 6
          !/\/bingo\/|\/casino\/|\/compte\/|\/account\/|\/connexion\/|\/log-in\/|\/inscription\/|\/registration\/|\/erreur\/|\/error\//i.test(
            urlDest
          )
        ) {
          if (/\/lel\//i.test(urlDest)) {
            let domainLoteries = process.env.VUE_APP_LOTERIES_DOMAIN;
            domainLoteries = domainLoteries.replace('.lotoquebec.', '.espacejeux.');
            window.location.href = nettoyerLienExterne(`${domainLoteries}${urlDest}`);
          } else {
            window.location.href = nettoyerLienExterne(`/${i18n.locale}${urlDest}`);
          }
        } else {
          next({ path: urlDest });
        }
      } else {
        // Fix redirect avec opl=1 si on est connecté
        const query = { ...to.query };
        delete query.opl;

        next({
          path: to.path,
          query,
          hash: to.hash,
          replace: true,
        });
      }
    } else if (!isSessionEstOuverte) {
      // Permet ouvrir page connexion en provenance AppMobile
      delete to.query.opl;
      try {
        // On vérifie que ciam (auth0) est actif via la variable dans template.env
        if (process.env.VUE_APP_CIAM_ENABLED === 'true') {
          let redirectUrl = `https:${nettoyerLienExterne(erreurDefaut.url)}`;
          const { dest } = to.query;
          if (dest) {
            if (/(connexion|log-in)/i.test(dest)) {
              // On supprime le query param dest avant la redirection vers Auth0
              delete to.query.dest;
              const pathname = store.getters['routes/getUrlFromWebsitePath'](route.websitePath);
              redirectUrl = `${process.env.VUE_APP_PROXY_URL_POD}/${i18n.locale}${pathname}`;
            } else {
              // On supprime le query param dest avant la redirection vers Auth0
              delete to.query.dest;
              // On récupère l'url de destination
              const urlDest = redirectionDest(to, dest);
              if (urlDest) {
                if (/\/lel\//i.test(urlDest)) {
                  let domainLoteries = process.env.VUE_APP_LOTERIES_DOMAIN;
                  domainLoteries = domainLoteries.replace('.lotoquebec.', '.espacejeux.');
                  redirectUrl = `${domainLoteries}${urlDest}`;
                } else {
                  redirectUrl = `${process.env.VUE_APP_PROXY_URL_POD}/${i18n.locale}${urlDest}`;
                }
              } else {
                redirectUrl = `https:${nettoyerLienExterne(erreurDefaut.url)}`;
              }
            }
          } else {
            redirectUrl = `${process.env.VUE_APP_PROXY_URL_POD}/${i18n.locale}${to.path}`;

            if (Object.keys(to.query).length > 0) {
              redirectUrl = `${redirectUrl}?${qs.stringify(to.query)}`;
            }

            if (to.hash) {
              redirectUrl = `${redirectUrl}${to.hash}`;
            }
          }
          // eslint-disable-next-line no-undef
          lancerConnexionAuth0(`${i18n.locale}`, `${redirectUrl}`);
        } else {
          next({
            path: store.getters['routes/getUrlFromWebsitePath'](PageMapping.CONNEXION.siteUrl),
            query: to.query,
          });
        }
      } catch (error) {
        console.error("Erreur à l'ouverture de la page de connexion Auth0", error);
      }
    } else if (
      to.path.startsWith(PageMapping.CONNEXION.siteUrl) &&
      hasQueryParams(to) &&
      to.query.dest
    ) {
      next({ query: to.query });
    }
  } else if (
    isSessionEstOuverte &&
    !hasProductSourceParam &&
    hasQueryParams(from) &&
    from.query.dest
  ) {
    const { dest } = from.query;
    // On supprime les query params avant la redirection
    delete from.query.dest;
    // On récupère l'url de destination
    const urlDest = redirectionDest(from, dest);
    if (!urlDest) {
      window.location.href = nettoyerLienExterne(erreurDefaut.url);
    } else {
      next({ path: urlDest });
    }
  } else if (!hasQueryParams(to) && hasQueryParams(from)) {
    // IMPORTANT: Ceci a pour effet de faire une redirection qui entrera à nouveau dans router.beforeEach
    // Inspiration: https://stackoverflow.com/questions/45091380/vue-router-keep-query-parameter-and-use-same-view-for-children
    // Si on a un query parameter, on le passe automatiquement à la route suivante
    if (
      from.query.mode &&
      from.path.startsWith(PageMappingCasino.CASINO_PRODUIT.siteUrl) &&
      !to.path.startsWith(PageMappingCasino.CASINO_PRODUIT.siteUrl)
    ) {
      delete from.query.mode;
    }
    if (
      from.query.merchantRefNum &&
      from.query.sessionId &&
      from.query.status &&
      from.path.startsWith(PageMapping.IDENTITE_SERVICE_VALIDATION.siteUrl)
    ) {
      delete from.query.merchantRefNum;
      delete from.query.sessionId;
      delete from.query.status;
      if (from.query.statusMessage) {
        delete from.query.statusMessage;
      }
    }
    next({ ...to, query: from.query });
  } else if (
    to.path &&
    to.query[ProductSourceInfo.PRODUCT_SOURCE] &&
    to.path == store.getters['routes/getUrlFromWebsitePath'](PageMapping.BIENVENUE.siteUrl) &&
    isProductSource2eChance() &&
    !newRegFlow
  ) {
    redirectionAccueil2eChance(to).catch((error) => {
      if (error.url) {
        next({
          path: store.getters['routes/getUrlFromWebsitePath'](error.url),
          query: to.query,
        });
      }
    });
  } else if (
    //   to.query.promo &&
    //   to.path ==
    //     store.getters['routes/getUrlFromWebsitePath'](PageMapping.INFOS_PERSONNELLES.siteUrl) &&
    //   store.state.compte &&
    //   store.state.compte.codePromo == '' &&
    //   !isProductSource2eChance()
    // ) {
    //   // Cas particulier d'un ancien appel sur "infos-personnelles"
    //   // avec un code promo qu'on doit rediriger sur la page "bienvenue".
    //   // Spécifications: TTP13550 https://testtrackweb.loto-quebec.com/ttweb/index.html#default/18/issues/13617/overview
    //   next({
    //     path: store.getters['routes/getUrlFromWebsitePath'](PageMapping.BIENVENUE.siteUrl),
    //     query: to.query,
    //   });
    // } else if (
    to.query.voucher &&
    to.path !== store.getters['routes/getUrlFromWebsitePath'](PageMapping.LIMITE_DEPOT.siteUrl) &&
    to.path !==
      store.getters['routes/getUrlFromWebsitePath'](PageMapping.LIMITE_DEPOT_CHOIX.siteUrl) &&
    to.path !== store.getters['routes/getUrlFromWebsitePath'](PageMapping.DEPOT.siteUrl)
  ) {
    // On efface le query param 'voucher' si Erreur
    delete to.query.voucher;
    next({
      path: to.path,
      query: to.query,
    });
  } else if (
    hasQueryParams(from) &&
    hasQueryParams(to) &&
    from.query.mode &&
    to.query.mode &&
    from.path.startsWith(PageMappingCasino.CASINO_PRODUIT.siteUrl) &&
    !to.path.startsWith(PageMappingCasino.CASINO_PRODUIT.siteUrl)
  ) {
    delete to.query.mode;
    next({
      path: to.path,
      query: to.query,
    });
  } else {
    // Si la route est permise
    verifierProtection(route, routeFrom)
      .then(() => {
        if (to.path == from.path) {
          bus.$emit('afficherMessage');
        }
        next();
      })
      .catch((error) => {
        if (error.url) {
          if (
            error.url.startsWith('//') ||
            error.url.startsWith('http') ||
            error.url.startsWith('app://')
          ) {
            if (window.location.host.includes('.local')) {
              console.log(`Redirection désactivée sur .local: ${error.url}`);
            } else {
              window.location.href = nettoyerLienExterne(error.url);
            }
            return;
          }
          const pathDestErrorUrl = store.getters['routes/getUrlFromWebsitePath'](error.url);
          if (error.message) {
            // Si on ne change pas de page, bloquer le routage
            if (pathDestErrorUrl == from.path) {
              bus.$emit('afficherMessage', error.message); // S'il y a lieu
              next(false);
            } else {
              store.commit('message/ADD_MESSAGE_FLASH', error.message);
              next({ path: pathDestErrorUrl, query: to.query });
            }
          } else {
            next({ path: pathDestErrorUrl, query: to.query });
          }
        } else {
          console.error(error);
          store.commit('message/ADD_MESSAGE_FLASH', 'MSG04');

          next({
            path: getRouteWithQueryParams(
              to,
              from,
              store.getters['routes/getUrlFromWebsitePath'](PageMapping.ERREUR_GENERIQUE.siteUrl)
            ),
          });
        }
      });
  }
});

// Inspiration: https://stackoverflow.com/questions/45091380/vue-router-keep-query-parameter-and-use-same-view-for-children
function hasQueryParams(route) {
  // !!=cast boolean
  return !!Object.keys(route.query).length;
}

function getRouteWithQueryParams(to, from, toPath) {
  const toWithQuery = { path: toPath, query: from.query };
  return toWithQuery;
}

const erreurDefaut = {
  url: inAppMobile()
    ? i18n.t('hyperlien.appActionConnexion')
    : i18n.t('hyperlien.portailLotoQuebec'),
};

/**
 * Fonction de sauvegarde du code promo de la session
 * @param {*} from : Route origine
 */
// async function sauvegardeCodePromo(from) {
//   // Si la session est ouverte, retirer "promo" des params de l'URL
//   if (session.sessionEstOuverte()) {
//     delete from.query.promo;
//   } else {
//     // Persiste le codePromo dans le store pour la création du profile
//     store.commit('compte/SET_CODE_PROMO', from.query.promo);
//   }
// }

/**
 * Fonction de sauvegarde du code promo de la session
 * @param {*} routeDest : Route destination
 */
async function redirectionAccueil2eChance(routeDest) {
  // Aiguillage 2e chance on ne passe plus par la page bienvenue
  // On aiguille soit sur la page Courriel (Mobile) ou infos personnelles (Desktop Web)
  // Spécifications: https://lotoquebec.atlassian.net/browse/SWSPC-1761
  if (isJelMobile()) {
    throw { url: PageMapping.COURRIEL.siteUrl };
  } else {
    throw { url: PageMapping.INFOS_PERSONNELLES.siteUrl };
  }
}

/**
 * Fonction qui retourne l'url de redirection en fonction du param dest
 *
 * @param {*} route : route de destination
 * @param {*} dest : destination
 */

function redirectionDest(route, dest) {
  let urlDest = urlsDest[i18n.locale][dest];

  if (/(\/compte|\/inscription)/i.test(urlDest)) {
    const urlSplit = urlDest.split('?');
    const hashSplit = urlSplit[0].split('#');
    const url = store.getters['routes/getUrlFromWebsitePath'](hashSplit[0]);
    const params = urlSplit[1];
    urlDest = params ? `${url}?${params}` : url;
    urlDest = urlSplit[0].indexOf('#') > 0 ? `${urlDest}#${hashSplit[1]}` : urlDest;
  }
  if (Object.keys(route.query).length > 0) {
    // On ajoute les query params existants à l'url de redirection
    urlDest =
      urlDest.indexOf('?') > 0
        ? `${urlDest}&${qs.stringify(route.query)}`
        : `${urlDest}?${qs.stringify(route.query)}`;
  }
  return urlDest;
}

/**
 * Fonction d'aiguillage, peut faire des redirections sur d'autres pages
 * en fonction de la protection de la page et d'attributs du profil du joueur
 * @param {*} routeDest : Route destination
 */
async function verifierProtection(routeDest, routeFrom) {
  const path = routeDest.websitePath.replace(process.env.VUE_APP_SITE_BASENAME, '');
  const page = Object.values(PageMapping).find((obj) => {
    return obj.siteUrl == path;
  });

  console.log('[verifierProtection] path:', path, '| page trouvée:', !!page, '| estConnecte:', page?.protection?.estConnecte);
  // La route n'est pas protégée
  if (!page || !page.protection) {
    return;
  }

  // PRODUCT SOURCE
  const { productSource } = page.protection;
  if (productSource) {
    const productSourceParam = getProductSourceFromQueryParams();

    if (productSource.listeAuMoinsUnParmi) {
      const bAuMoinsUnTrouve = productSource.listeAuMoinsUnParmi.includes(productSourceParam);

      if (!bAuMoinsUnTrouve) {
        console.warn(`Accès refusé '${path}': productSource.listeAuMoinsUnParmi`);
        throw erreurDefaut;
      }
    }

    if (productSource.listeAbsent) {
      const bAuMoinsUnTrouve = productSource.listeAbsent.includes(productSourceParam);

      if (bAuMoinsUnTrouve) {
        console.warn(`Accès refusé '${path}': productSource.listeAbsent`);
        throw erreurDefaut;
      }
    }
  }

  if (page.protection.rafraichirProfile) {
    store.commit('compte/RESET');
    store.commit('profile/RESET');
  }

  // CONNECTÉ / NON-CONNECTÉ
  if (page.protection.estConnecte != null) {
    const isSessionEstOuverte = session.sessionEstOuverte();
    if (
      (isSessionEstOuverte && !page.protection.estConnecte) ||
      (!isSessionEstOuverte && page.protection.estConnecte)
    ) {
      console.warn(`Accès refusé '${path}': estConnecte`);
      throw erreurDefaut;
    }

    if (!page.protection.estConnecte) {
      return;
    }
  }

  // Page qui n'a pas besoin du profil pour s'afficher (les appels sont faits par la page au besoin)
  if (page.protection.sansChargementProfil) {
    console.log(`[sansChargementProfil] Page '${path}' : chargement du profil ignoré`);
    // Les status flags de la session (déjà chargée par l'entête) permettent de détecter un GUEST sans appel réseau
    if (page.protection.statusFlag?.listeAbsent?.includes(playerPaymentProfileStatus.GUEST)) {
      // ejApi et les status flags de la session sont fournis par l'entête; on attend qu'ils soient
      // lisibles (clientEstInvite() retourne false sans erreur si la session n'est pas encore prête)
      let dernierEtat = null;
      const statusFlagsLisibles = () => {
        let etat;
        let lisible = false;
        try {
          // eslint-disable-next-line no-undef
          if (typeof ejApi === 'undefined') {
            etat = "ejApi en attente (pas encore défini par l'entête)...";
          } else {
            // eslint-disable-next-line no-undef
            const flags = ejApi.lireInfoSession('status_flags');
            if (typeof flags === 'string') {
              etat = `status_flags de la session lus : '${flags}'`;
              lisible = true;
            } else {
              etat = `getSessionDetails en attente... (ejApi défini, status_flags non disponible : ${flags})`;
            }
          }
        } catch (e) {
          etat = `getSessionDetails en attente... (lecture de status_flags en erreur : ${e?.message || e})`;
        }
        // On ne loggue que les changements d'état pour ne pas inonder la console
        if (etat !== dernierEtat) {
          console.log(`[sansChargementProfil] ${etat}`);
          dernierEtat = etat;
        }
        return lisible;
      };
      const debutAttente = Date.now();
      if (!statusFlagsLisibles()) {
        await new Promise((resolve, reject) => {
          const fin = debutAttente + 10000;
          let prochainRappel = debutAttente + 1000;
          const verifier = () => {
            const ecoule = Date.now() - debutAttente;
            if (statusFlagsLisibles()) {
              console.log(
                `[sansChargementProfil] Session prête après ${ecoule} ms d'attente, vérification du statut GUEST`
              );
              resolve();
            } else if (Date.now() > fin) {
              console.log(
                `[sansChargementProfil] Délai dépassé après ${ecoule} ms : status_flags jamais disponibles, redirection vers la page d’erreur`
              );
              reject(new Error('Status flags de la session non disponibles'));
            } else {
              // Rappel chaque seconde pour montrer que l'attente se prolonge
              if (Date.now() >= prochainRappel) {
                console.log(
                  `[sansChargementProfil] Toujours en attente de la session... ${(ecoule / 1000).toFixed(1)} s écoulées`
                );
                prochainRappel += 1000;
              }
              setTimeout(verifier, 50);
            }
          };
          setTimeout(verifier, 50);
        });
      } else {
        console.log('[sansChargementProfil] Session déjà prête (aucune attente)');
      }
      // eslint-disable-next-line no-undef
      const estInvite = ejApi.clientEstInvite();
      if (estInvite) {
        console.log(
          `[sansChargementProfil] Client GUEST détecté : redirection vers ${PageMapping.IDENTITE_REPRISE.siteUrl}`
        );
        throw { url: PageMapping.IDENTITE_REPRISE.siteUrl };
      }
      // Client non GUEST : les limites de dépôt doivent être définies, sinon on redirige vers la page de limite de dépôt
      const limites = await encadrerJeux.getLimiteJeux('deposit');
      if (limites?.deposit?.week?.actuelLimit == null) {
        console.log(
          `[sansChargementProfil] Limite de dépôt non définie : redirection vers ${PageMapping.LIMITE_DEPOT.siteUrl}`
        );
        throw { url: PageMapping.LIMITE_DEPOT.siteUrl };
      }
      console.log('[sansChargementProfil] Client non GUEST : accès à la page autorisé');
    }
    return;
  }

  // Les vérifications qui suivent assument qu'une connexion valide est établie
  const bDepositInfo = !!page.protection.obtenirDepotInfo;
  const bWithdrawInfo = !!page.protection.obtenirRetraitInfo;

  const playerPaymentProfile = await loadPlayerPaymentProfile({
    depositInfo: bDepositInfo,
    withdrawInfo: bWithdrawInfo,
    forceRefresh: page.forceRefreshPlayersProfile,
  });

  // ***** PROFESSION *****
  if (page.protection.aProfession != null) {
    // Pour des raisons de performance, uniquement pour un client "joueur" (non-guest),
    // le micro-service retourne toujours "true" pour "hasProfession".
    // Dans ce cas, on doit donner l'accès à Profession/Canafe qu'elle soit définie ou non.
    // Joueurs sans profession : 3e partie pour vérifier si un joueur sans profession a déjà
    // setté sa profession dans le store et empêcher l'accès à la page 'identite-legislation-federale'
    if (
      (page.protection.aProfession && !playerPaymentProfile.hasProfession) ||
      (!page.protection.aProfession &&
        playerPaymentProfile.hasProfession &&
        playerPaymentProfile.status.includes(playerPaymentProfileStatus.GUEST)) ||
      (page.protection.joueurException && store.state.profile.joueurException)
    ) {
      console.warn(
        `Accès refusé: protection.aProfession=${page.protection.aProfession}, playerPaymentProfile.hasProfession=${playerPaymentProfile.hasProfession}`
      );
      throw getClientRedirect(playerPaymentProfile, routeDest);
    }
  }

  // Migration. Verification si le client est deja enregistré dans Salesforce
  if (
    (page.useSalesforceMigration && store.state.dossier.clientMigrated === null) ||
    (page.forceRefreshSalesforceProfile && !store.state.dossier.clientMigrated)
  ) {
    await processSalesforceClientMigrated();
  }

  // ***** STATUS FLAGS *****
  const { statusFlag } = page.protection;
  if (statusFlag != null) {
    if (statusFlag.listeObligatoire != null) {
      let bTousTrouves = statusFlag.listeObligatoire.every(
        (elem) => playerPaymentProfile.status.indexOf(elem) > -1
      );
      if (bTousTrouves && statusFlag.obligatoireExclusif) {
        bTousTrouves = playerPaymentProfile.status.length == statusFlag.listeObligatoire.length;
      }

      if (!bTousTrouves) {
        console.warn(`Accès refusé '${path}': statusFlag.listeObligatoire`);
        throw getClientRedirect(playerPaymentProfile, routeDest);
      }
    }

    if (statusFlag.listeAuMoinsUnParmi != null) {
      let bAuMoinsUnParmi = null;
      bAuMoinsUnParmi = statusFlag.listeAuMoinsUnParmi.some((elem) =>
        playerPaymentProfile.status.includes(elem)
      );

      if (!bAuMoinsUnParmi) {
        console.warn(`Accès refusé '${path}': statusFlag.listeAuMoinsUnParmi`);
        throw getClientRedirect(playerPaymentProfile, routeDest);
      }
    }

    if (statusFlag.listeAbsent != null) {
      let bAuMoinsUnAbsentTrouve = null;
      bAuMoinsUnAbsentTrouve = statusFlag.listeAbsent.some((elem) =>
        playerPaymentProfile.status.includes(elem)
      );

      if (bAuMoinsUnAbsentTrouve) {
        if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.GUEST)) {
          throw { url: PageMapping.IDENTITE_REPRISE.siteUrl };
        }
        console.warn(`Accès refusé '${path}': statusFlag.listeAbsent`);
        throw getClientRedirect(playerPaymentProfile, routeDest);
      }
    }
  }

  // ***** ADRESSE *****
  if (page.protection.obtenirAdresse) {
    /**
     * On ne récupère pas l'adresse de la BD OpenBet si on vient des pages:
     * - /compte/carte-adresse-suggestion
     * - /compte/docunment-adresse-suggestion
     * */
    if (
      !routeFrom ||
      (routeFrom &&
        ![
          PageMapping.IDENTITE_ADRESSE_SUGGESTION.siteUrl,
          PageMapping.IDENTITE_DOCUMENT_ADRESSE_SUGGESTION.siteUrl,
        ].includes(routeFrom.websitePath.substring(4)))
    ) {
      if (store.state.dossier.clientMigrated === null) {
        await processSalesforceClientMigrated();
      }
      const aAdresse = await store
        .dispatch('profile/getAddress')
        .then((addressInfo) => {
          store.commit('compte/SET_ADDRESS_FROM_ADDRESS_PROFILE', addressInfo);
          return addressInfo.addressLine1 != '';
        })
        .catch(() => {
          return null;
        });
      if (aAdresse == null) {
        throw { url: PageMapping.ERREUR_GENERIQUE.siteUrl };
      }

      if (page.protection.aAdresse != null) {
        if ((page.protection.aAdresse && !aAdresse) || (!page.protection.aAdresse && aAdresse)) {
          console.warn(`Accès refusé '${path}': aAdresse`);
          throw getClientRedirect(playerPaymentProfile, routeDest);
        }
      }
    }
  }

  // ***** DÉPÔT *****
  if (page.protection.obtenirDepotInfo) {
    if (!playerPaymentProfile.depositInfo) {
      // Ne devrait pas arriver
      throw { url: PageMapping.ERREUR_GENERIQUE.siteUrl };
    }

    if (playerPaymentProfile.depositInfo.hasDepositLimit) {
      if (
        playerPaymentProfile.depositInfo.cardAllowDeposit == false ||
        playerPaymentProfile.depositInfo.isBlocked ||
        playerPaymentProfile.status.includes(playerPaymentProfileStatus.DEPOSIT_BLOCKED)
      ) {
        recupererMessage('MSG04-50', true, PageMapping.DEPOT_NON_AUTORISE.siteUrl);
      }

      if (page.protection.aLimiteDepot != null && !page.protection.aLimiteDepot) {
        throw { url: PageMapping.DEPOT.siteUrl };
      }
    } else if (page.protection.aLimiteDepot) {
      throw { url: PageMapping.LIMITE_DEPOT.siteUrl };
    }
  }

  // ***** RETRAIT *****
  if (page.protection.obtenirRetraitInfo) {
    if (
      !playerPaymentProfile.withdrawalInfo ||
      !playerPaymentProfile.withdrawalInfo.withdrawalAuthStatus ||
      playerPaymentProfile.withdrawalInfo.withdrawalAuthStatus != 'YES' ||
      playerPaymentProfile.withdrawalInfo.isBlocked ||
      playerPaymentProfile.status.includes(playerPaymentProfileStatus.WITHDRAWAL_BLOCKED)
    ) {
      throw erreurDefaut;
    }
  }

  // TODO: Cas joueur sans profession => brancher directement sur profession
}

// Specs: https://lotoquebec.atlassian.net/browse/SWSPC-1544
function getClientRedirect(playerPaymentProfile, routeDest) {
  if (
    !playerPaymentProfile.status.includes(playerPaymentProfileStatus.GUEST) &&
    !playerPaymentProfile.hasProfession
  ) {
    // Pour des raisons de performance, uniquement pour un client "joueur" (non-guest),
    // le micro-service retourne toujours "true" pour "hasProfession".
    // Dans ce cas, on doit donner l'accès à Profession/Canafe qu'elle soit définie ou non.
    return {
      url: PageMapping.IDENTITE_PROFESSION.siteUrl,
    };
  }

  // Si client joueur (non-guest)
  if (!playerPaymentProfile.status.includes(playerPaymentProfileStatus.GUEST)) {
    if (getProductSourceFromQueryParams() !== null) {
      return redirigerVersURLRetourConnexion();
    }
    return erreurDefaut;
  }

  if (
    !playerPaymentProfile.hasProfession &&
    (playerPaymentProfile.status.includes(playerPaymentProfileStatus.UPLOAD_ID) ||
      playerPaymentProfile.status.includes(playerPaymentProfileStatus.FAILED_OPTIMAL) ||
      playerPaymentProfile.status.includes(playerPaymentProfileStatus.PENDING_ID) ||
      playerPaymentProfile.status.includes(playerPaymentProfileStatus.PENDING_OPTIMAL))
  ) {
    return {
      url: PageMapping.IDENTITE_PROFESSION.siteUrl,
    };
  }

  if (!playerPaymentProfile.hasProfession) {
    return {
      url: PageMapping.IDENTITE_REPRISE.siteUrl,
    };
  }

  if (playerPaymentProfile.hasProfession) {
    /*
     * Modifier la logique de navigation pour le flag UploadID
     * Spécifications: https://lotoquebec.atlassian.net/browse/PVM-33
     */
    if (inAppMobile()) {
      if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.FAILED_OPTIMAL)) {
        if (
          playerPaymentProfile.status.includes(playerPaymentProfileStatus.FAILED_VERIFIEDME) &&
          playerPaymentProfile.status.includes(playerPaymentProfileStatus.UPLOAD_ID)
        ) {
          return {
            url: PageMapping.IDENTITE_DOCUMENTS_ENVOYES.siteUrl,
          };
        }
        return {
          url: PageMapping.IDENTITE_REPRISE_ECHEC_APP.siteUrl,
        };
      }
    }
    if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.UPLOAD_ID)) {
      if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.FAILED_VERIFIEDME)) {
        return {
          url: PageMapping.IDENTITE_DOCUMENTS_ENVOYES.siteUrl,
        };
      }
      if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.FAILED_OPTIMAL)) {
        return {
          url: PageMapping.IDENTITE_DOCUMENTS_ENVOYES_SERVICE.siteUrl,
        };
      }
      return {
        url: PageMapping.IDENTITE_DOCUMENTS_ENVOYES.siteUrl,
      };
    }

    if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.FAILED_OPTIMAL)) {
      if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.FAILED_VERIFIEDME)) {
        return {
          url: PageMapping.IDENTITE_REPRISE_DOCUMENTS.siteUrl,
        };
      }
      return {
        url: PageMapping.IDENTITE_OPTIONS_VERIFICATION.siteUrl,
      };
    }

    if (playerPaymentProfile.status.includes(playerPaymentProfileStatus.PENDING_ID)) {
      return {
        url: PageMapping.IDENTITE_REPRISE_DOCUMENTS.siteUrl,
      };
    }
    return {
      url: PageMapping.IDENTITE_ADRESSE.siteUrl,
    };
  }

  return erreurDefaut;
}

function redirigerVersURLRetourConnexion() {
  // eslint-disable-next-line no-undef
  obtenirSiteIds()
    .then((response) => {
      if (response && response.data.URLRetourConnexion) {
        return { url: response.data.URLRetourConnexion };
      }
      return { url: erreurDefaut.url };
    })
    .catch(() => {
      return { url: erreurDefaut.url };
    });
}

function isRouting() {
  return process.env.VUE_APP_IS_ROUTING === 'true';
}

function recupererMessage(msgCode, sauverMessage = false, redirectUrl = null) {
  let errorMessage = null;
  // On récupère le message pour l'affichage dans l'iframe Paysafe
  MessageHelpers.obtenirMessage(msgCode, document.$root)
    .then((msgResp) => {
      const msg = {
        title: msgResp.msgTitre,
        details: MessageHelpers.nettoyerMessageSpanP(msgResp.msgDetail),
      };
      errorMessage = msg;
      if (sauverMessage) {
        if ([PageMapping.DEPOT_NON_AUTORISE.siteUrl].includes(redirectUrl)) {
          store.commit('profile/SET_MESSAGE_DEPOT', msg);
        } else if ([PageMapping.DEPOT_ARGENT_WEB_NON_AUTORISE.siteUrl].includes(redirectUrl)) {
          store.commit('profile/SET_MESSAGE_VOUCHER', msg);
        }
      }

      return msgResp;
    })
    .catch((error) => {
      console.log('Erreur de récupération du message', error);
    })
    .finally(() => {
      if (
        [
          PageMapping.DEPOT_NON_AUTORISE.siteUrl,
          PageMapping.DEPOT_ARGENT_WEB_NON_AUTORISE.siteUrl,
        ].includes(redirectUrl) ||
        redirectUrl
      ) {
        throw { url: redirectUrl, message: errorMessage };
      }
    });
}

export default router;
